import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Redirector() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [error, setError] = useState("");
    const [pending, setPending] = useState(null); // {pending_id}
    const [passwordRequired, setPasswordRequired] = useState(false);
    const [pw, setPw] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const resolve = async (password) => {
        setSubmitting(true);
        setError("");
        try {
            const { data } = await api.get(`/r/${slug}`, { params: password ? { pw: password } : {} });
            if (data.long_url) {
                window.location.replace(data.long_url);
                return;
            }
        } catch (e) {
            if (e.response?.status === 401 && e.response?.data?.password_required) {
                setPasswordRequired(true);
            } else if (e.response?.status === 409 && e.response?.data?.pending_activation) {
                setPending({ pending_id: e.response.data.pending_id });
            } else if (e.response?.status === 404) {
                setError("This link doesn't exist.");
            } else if (e.response?.status === 410) {
                setError("This link has expired.");
            } else {
                setError(e.response?.data?.detail || "Something went wrong.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => { resolve(null); /* eslint-disable-next-line */ }, [slug]);

    return (
        <div className="flex min-h-screen items-center justify-center bg-background p-6">
            <div className="w-full max-w-md">
                <div className="mb-8 flex justify-center"><Logo /></div>
                <div className="border-2 border-border bg-card p-8">
                    {pending ? (
                        <>
                            <div className="text-xs uppercase tracking-widest text-[hsl(27,96%,45%)]">Preview link</div>
                            <h1 className="mt-2 font-display text-3xl font-bold tracking-tighter">Not activated yet</h1>
                            <p className="mt-2 text-sm text-muted-foreground">
                                The person who generated <span className="font-mono">/{slug}</span> hasn't activated it yet.
                                It becomes a real short URL only when they register a Splink.pro account (within 30 min of creating the preview).
                            </p>
                            <div className="mt-6 flex gap-2">
                                <Button asChild className="flex-1" data-testid="pending-page-register-btn">
                                    <a href={`/register?pending=${encodeURIComponent(pending.pending_id)}`}>Register &amp; activate</a>
                                </Button>
                                <Button variant="outline" onClick={() => navigate("/")} className="flex-1" data-testid="pending-page-home-btn">Go home</Button>
                            </div>
                        </>
                    ) : passwordRequired ? (
                        <>
                            <div className="text-xs uppercase tracking-widest text-muted-foreground">Protected link</div>
                            <h1 className="mt-2 font-display text-3xl font-bold tracking-tighter">Enter password</h1>
                            <p className="mt-2 text-sm text-muted-foreground">
                                This link requires a password to continue.
                            </p>
                            <form className="mt-6 space-y-4" onSubmit={(e) => { e.preventDefault(); resolve(pw); }}>
                                <div>
                                    <Label htmlFor="pw-input">Password</Label>
                                    <Input id="pw-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-2 h-11" data-testid="resolve-password-input" required />
                                </div>
                                {error && <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>}
                                <Button type="submit" disabled={submitting} className="h-11 w-full" data-testid="resolve-submit-btn">
                                    {submitting ? "Checking..." : "Continue"}
                                </Button>
                            </form>
                        </>
                    ) : error ? (
                        <>
                            <div className="text-xs uppercase tracking-widest text-destructive">Error</div>
                            <h1 className="mt-2 font-display text-3xl font-bold tracking-tighter">{error}</h1>
                            <Button onClick={() => navigate("/")} className="mt-6 w-full" data-testid="resolve-home-btn">Back to Splink.pro</Button>
                        </>
                    ) : (
                        <div className="py-6 text-center font-mono text-sm text-muted-foreground">Redirecting…</div>
                    )}
                </div>
            </div>
        </div>
    );
}
